import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Checkbox, FormControlLabel, TextField, InputAdornment,
  CircularProgress, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SectionCard from '../SectionCard';
import StatusChip from '../StatusChip';
import { getPurchasableVendors } from '../../api/vendor';
import { vendorDisplayLabel } from '../../utils/rfqEntity';
import { IPD_COLORS, inputSx } from '../../theme';

export default function RfqVendorSelector({
  selectedIds = [],
  onChange,
  preselectedId,
  disabled = false,
  error,
}) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPurchasableVendors()
      .then((list) => {
        if (!cancelled) setVendors(list);
      })
      .catch(() => { if (!cancelled) setVendors([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!preselectedId || selectedIds.includes(preselectedId)) return;
    onChange?.([...selectedIds, preselectedId]);
  }, [preselectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) => {
      const hay = [v.vendor_name, v.vendor_code, v.vendor_type, v.city]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [vendors, search]);

  function toggle(id) {
    if (disabled) return;
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  function selectAll() {
    if (disabled) return;
    onChange(filtered.map((v) => v.vendor_id || v.id));
  }

  function clearAll() {
    if (disabled) return;
    onChange([]);
  }

  return (
    <SectionCard
      title="Invite Vendors"
      subtitle="Only approved vendors eligible for purchase orders"
      sx={{ minHeight: { md: 420 } }}
      actions={(
        <Chip
          icon={<CheckCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
          label={`${selectedIds.length} selected`}
          size="small"
          sx={{
            fontWeight: 700,
            backgroundColor: selectedIds.length > 0 ? IPD_COLORS.success.bg : IPD_COLORS.cardBg,
            color: selectedIds.length > 0 ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
          }}
        />
      )}
    >
      <TextField
        fullWidth
        size="small"
        placeholder="Search vendor name or code…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        disabled={disabled || loading}
        sx={{ ...inputSx, mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      />

      {!disabled && filtered.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Typography
            component="button"
            type="button"
            onClick={selectAll}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer', p: 0,
              fontSize: 12, fontWeight: 600, color: IPD_COLORS.primary,
            }}
          >
            Select all visible
          </Typography>
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>·</Typography>
          <Typography
            component="button"
            type="button"
            onClick={clearAll}
            sx={{
              border: 'none', background: 'none', cursor: 'pointer', p: 0,
              fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary,
            }}
          >
            Clear
          </Typography>
        </Box>
      )}

      {error && (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.error.text, mb: 1 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
        </Box>
      ) : vendors.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.warning.text, py: 2 }}>
          No approved vendors available. Complete vendor approval before creating an RFQ.
        </Typography>
      ) : filtered.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
          No vendors match your search.
        </Typography>
      ) : (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
        }}>
          {filtered.map((v) => {
            const id = v.vendor_id || v.id;
            const checked = selectedSet.has(id);
            return (
              <Box
                key={id}
                onClick={() => toggle(id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  border: `1px solid ${checked ? IPD_COLORS.primary : IPD_COLORS.border}`,
                  backgroundColor: checked ? IPD_COLORS.selectedBg : IPD_COLORS.cardBg,
                  cursor: disabled ? 'default' : 'pointer',
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  '&:hover': disabled ? {} : { borderColor: IPD_COLORS.primary },
                }}
              >
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
                        {vendorDisplayLabel(v)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                        {[v.vendor_type, v.city].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  )}
                  sx={{ m: 0, flex: 1 }}
                />
                <StatusChip status="APPROVED" />
              </Box>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
}
